<!-- eslint-disable vue/no-mutating-props -->
<!-- 商品、套餐、分类、规格、加料与库存维护的弹窗模块。表单状态由父页面集中管理。 -->
<template>
  <el-dialog
    :model-value="productEditorVisible"
    :title="isEditingProduct ? '编辑商品' : '新增商品'"
    width="760px"
    @update:model-value="emit('update:productEditorVisible', $event)"
  >
    <div class="detail-grid detail-grid--single">
      <article class="detail-card">
        <div class="detail-actions">
          <GuidedField label="商品名称" hint="展示在商品列表、购物车和订单中。" required>
            <el-input v-model.trim="productForm.name" placeholder="如 冰美式" />
          </GuidedField>
          <GuidedField label="所属门店" hint="每个商品只能归属一个门店。" required>
            <el-select v-model="productForm.storeId" filterable placeholder="请选择门店" @change="emit('productStoreChanged')">
              <el-option v-for="store in storeOptions" :key="store.id" :label="store.name" :value="store.id" />
            </el-select>
          </GuidedField>
          <GuidedField label="商品分类" hint="只展示当前门店可用的分类。" required>
            <el-select v-model="productForm.categoryId" :disabled="!productForm.storeId" placeholder="请选择分类">
              <el-option v-for="category in productCategoryOptions" :key="category.id" :label="category.name" :value="category.id" />
            </el-select>
          </GuidedField>
          <GuidedField label="副标题" hint="商品名称下方的补充卖点，选填。">
            <el-input v-model.trim="productForm.subtitle" placeholder="如 现磨咖啡，低糖清爽" />
          </GuidedField>
          <GuidedField label="销售状态" hint="下架后用户端不可购买。">
            <el-select v-model="productForm.status">
              <el-option label="在售" value="ON" />
              <el-option label="下架" value="OFF" />
            </el-select>
          </GuidedField>
          <GuidedField label="支持外卖" hint="开启后该商品可在小程序外卖菜单中展示。">
            <el-select v-model="productForm.supportsDelivery">
              <el-option label="支持外卖" :value="true" />
              <el-option label="仅自取" :value="false" />
            </el-select>
          </GuidedField>
          <GuidedField label="需选规格" hint="选择「不需要」后，小程序加购该商品不再弹「可选糖/温度」弹窗（零食、包装商品用）。">
            <el-select v-model="productForm.needSpec">
              <el-option label="需要（饮品）" :value="true" />
              <el-option label="不需要（零食/包装）" :value="false" />
            </el-select>
          </GuidedField>
          <GuidedField label="商品主图" hint="从本地选择图片，用于商品展示。" wide required>
            <LocalImagePicker v-model="productForm.mainImageUrl" button-text="选择商品主图" />
          </GuidedField>
          <GuidedField label="商品描述" hint="补充口味、规格或使用说明，选填。" wide>
            <el-input v-model.trim="productForm.description" type="textarea" :rows="3" placeholder="如 中度烘焙，带有坚果香气" />
          </GuidedField>
          <div class="spec-editor detail-actions__full">
            <div class="spec-editor__heading">
              <div>
                <strong>规格配置</strong>
                <small>为当前商品单独配置可选糖、温度偏好、精选小料的具体选项。</small>
              </div>
            </div>
            <div v-for="(group, groupIndex) in productForm.specGroups" :key="group.id || groupIndex" class="spec-group">
              <div class="spec-group__header">
                <strong>{{ group.name }}</strong>
                <small>{{ group.required ? '必选' : '非必选' }} / {{ group.multiple ? '多选' : '单选' }}</small>
              </div>
              <div v-if="group.id === 'topping'" class="spec-option-list">
                <el-select
                  :model-value="selectedToppingExtraIds(group.options)"
                  multiple
                  filterable
                  placeholder="从加料管理中选择"
                  @update:model-value="handleProductToppingChange"
                >
                  <el-option
                    v-for="extra in extraOptions"
                    :key="extra.id"
                    :label="extra.name"
                    :value="extra.id"
                    :disabled="extra.status === 'DISABLED'"
                  />
                </el-select>
                <p v-if="!extraOptions.length" class="detail-tip">暂无可选小料，请先到加料管理添加。</p>
              </div>
              <div v-else class="spec-option-list">
                <div v-for="(option, optionIndex) in group.options" :key="option.id || optionIndex" class="spec-option">
                  <el-select
                    v-if="specPresetNames(group.id).length"
                    v-model="option.name"
                    filterable
                    placeholder="请选择"
                  >
                    <el-option
                      v-for="preset in specPresetNames(group.id)"
                      :key="preset"
                      :label="preset"
                      :value="preset"
                    />
                  </el-select>
                  <el-input v-else v-model.trim="option.name" placeholder="选项名称" />
                  <el-input v-model.number="option.priceInFen" type="number" step="0.01" placeholder="加价（元）" />
                  <el-select v-model="option.enabled">
                    <el-option label="启用" :value="true" />
                    <el-option label="停用" :value="false" />
                  </el-select>
                  <el-select v-model="option.recommended">
                    <el-option label="普通" :value="false" />
                    <el-option label="推荐" :value="true" />
                  </el-select>
                  <el-button text type="danger" @click="emit('removeProductSpecOption', groupIndex, optionIndex)">删除</el-button>
                </div>
                <el-button plain size="small" @click="emit('appendProductSpecOption', groupIndex)">添加选项</el-button>
              </div>
            </div>
          </div>
          <GuidedField label="原价" hint="金额单位为元，例如 25 表示 ¥25.00。">
            <el-input v-model.number="productForm.originalPrice" type="number" step="0.01" placeholder="单位：元" />
          </GuidedField>
          <GuidedField label="库存" :hint="productStockHint">
            <el-input v-model.number="productForm.stock" type="number" placeholder="如 100" />
          </GuidedField>
        </div>
      </article>
    </div>
    <template #footer>
      <el-button @click="emit('update:productEditorVisible', false)">取消</el-button>
      <el-button type="primary" :loading="productSubmitting" :disabled="!productForm.name || !productForm.categoryId || !productForm.storeId || !productForm.mainImageUrl" @click="emit('submitProduct')">
        {{ isEditingProduct ? '保存修改' : '创建商品' }}
      </el-button>
    </template>
  </el-dialog>

  <el-dialog
    :model-value="comboEditorVisible"
    :title="isEditingCombo ? '编辑商品套餐' : '新增商品套餐'"
    width="820px"
    @update:model-value="emit('update:comboEditorVisible', $event)"
  >
    <div class="detail-grid detail-grid--single">
      <article class="detail-card">
        <div class="detail-actions">
          <GuidedField label="套餐名称" hint="展示在用户端的组合商品名称。" required>
            <el-input v-model.trim="comboForm.name" placeholder="如 早餐组合" />
          </GuidedField>
          <GuidedField label="适用门店" hint="店长新建时必须选择授权门店；平台角色可留空表示全部门店。">
            <el-select v-model="comboStoreIds" multiple filterable placeholder="全部门店">
              <el-option v-for="store in storeOptions" :key="store.id" :label="store.name" :value="store.id" />
            </el-select>
          </GuidedField>
          <GuidedField label="原价" hint="套餐内商品原价合计，金额单位为元。">
            <el-input v-model.number="comboForm.originalPrice" type="number" step="0.01" placeholder="单位：元" />
          </GuidedField>
          <GuidedField label="套餐售价" hint="用户实际支付的套餐价格，金额单位为元。">
            <el-input v-model.number="comboForm.salePrice" type="number" step="0.01" placeholder="单位：元" />
          </GuidedField>
          <GuidedField label="套餐状态" hint="停用后用户端不可购买。">
            <el-select v-model="comboForm.status">
              <el-option label="启用" value="ENABLED" />
              <el-option label="停用" value="DISABLED" />
            </el-select>
          </GuidedField>
        </div>
      </article>
      <article class="detail-card">
        <h3>套餐商品</h3>
        <div class="detail-actions">
          <GuidedField label="套餐商品" hint="选择要加入套餐的商品。" required>
            <el-select v-model="comboItemForm.productId">
              <el-option v-for="product in productOptions" :key="product.id" :label="product.name" :value="product.id" />
            </el-select>
          </GuidedField>
          <GuidedField label="商品数量" hint="该商品在套餐中包含的份数。">
            <el-input v-model.number="comboItemForm.quantity" type="number" placeholder="如 1" />
          </GuidedField>
          <el-button type="primary" @click="emit('appendComboItem')">添加商品</el-button>
        </div>
        <div class="detail-items">
          <div v-for="(item, index) in comboForm.items" :key="`${item.productId}-${index}`" class="detail-item">
            <div class="detail-item__meta">
              <strong>{{ item.productName || item.productId }}</strong>
              <small>商品 ID {{ item.productId }} / 数量 {{ item.quantity ?? 1 }} / {{ productNeedSpecText(item.productId) }}</small>
            </div>
            <div class="detail-item__right">
              <button @click="emit('removeComboItem', index)">删除</button>
            </div>
          </div>
        </div>
      </article>
    </div>
    <template #footer>
      <el-button @click="emit('update:comboEditorVisible', false)">取消</el-button>
      <el-button type="primary" :loading="comboSubmitting" :disabled="!comboForm.name" @click="emit('submitCombo')">
        {{ isEditingCombo ? '保存修改' : '创建套餐' }}
      </el-button>
    </template>
  </el-dialog>

  <el-dialog
    :model-value="categoryEditorVisible"
    :title="isEditingCategory ? '编辑分类' : '新增分类'"
    width="560px"
    @update:model-value="emit('update:categoryEditorVisible', $event)"
  >
    <div class="detail-grid detail-grid--single">
      <article class="detail-card">
        <div class="detail-actions">
          <GuidedField label="分类名称" hint="展示在商品菜单中的分类名称。" required>
            <el-input v-model.trim="categoryForm.name" placeholder="如 咖啡饮品" />
          </GuidedField>
          <GuidedField label="指定门店" hint="不选择表示所有门店都展示该分类。">
            <el-select v-model="categoryStoreIds" multiple filterable placeholder="全部门店">
              <el-option v-for="store in storeOptions" :key="store.id" :label="store.name" :value="store.id" />
            </el-select>
          </GuidedField>
          <GuidedField label="菜单标识" hint="分类统一归属经典菜单，不可修改。">
            <el-input :model-value="menuTabLabel" disabled />
          </GuidedField>
          <GuidedField label="排序值" hint="数值越小越靠前。">
            <el-input v-model.number="categoryForm.sort" type="number" placeholder="如 10" />
          </GuidedField>
          <GuidedField label="分类状态" hint="停用后用户端不显示该分类。">
            <el-select v-model="categoryForm.status">
              <el-option label="启用" value="ENABLED" />
              <el-option label="停用" value="DISABLED" />
            </el-select>
          </GuidedField>
          <GuidedField label="支持外卖" hint="开启后该分类会在小程序外卖菜单中展示。">
            <el-select v-model="categoryForm.supportsDelivery">
              <el-option label="支持外卖" :value="true" />
              <el-option label="仅自取" :value="false" />
            </el-select>
          </GuidedField>
        </div>
      </article>
    </div>
    <template #footer>
      <el-button @click="emit('update:categoryEditorVisible', false)">取消</el-button>
      <el-button type="primary" :loading="categorySubmitting" :disabled="!categoryForm.name" @click="emit('submitCategory')">
        {{ isEditingCategory ? '保存修改' : '创建分类' }}
      </el-button>
    </template>
  </el-dialog>

  <el-dialog
    :model-value="extraEditorVisible"
    :title="isEditingExtra ? '编辑加料' : '新增加料'"
    width="640px"
    @update:model-value="emit('update:extraEditorVisible', $event)"
  >
    <div class="detail-grid detail-grid--single">
      <article class="detail-card">
        <div class="detail-actions">
          <GuidedField label="加料名称" hint="展示在商品加料选项中的名称。" required>
            <el-input v-model.trim="extraForm.name" placeholder="如 燕麦奶" />
          </GuidedField>
          <GuidedField label="适用门店" hint="店长新建时必须选择授权门店；平台角色可留空表示全部门店。">
            <el-select v-model="extraForm.storeId" clearable filterable placeholder="平台全局">
              <el-option v-for="store in storeOptions" :key="store.id" :label="store.name" :value="store.id" />
            </el-select>
          </GuidedField>
          <GuidedField label="加价金额" hint="用户选择该加料时增加的金额，单位为元。">
            <el-input v-model.number="extraForm.price" type="number" step="0.01" placeholder="单位：元" />
          </GuidedField>
          <GuidedField label="排序值" hint="数值越小越靠前。">
            <el-input v-model.number="extraForm.sort" type="number" placeholder="如 10" />
          </GuidedField>
          <GuidedField label="加料状态" hint="停用后用户端不再展示。">
            <el-select v-model="extraForm.enabled">
              <el-option label="启用" :value="true" />
              <el-option label="停用" :value="false" />
            </el-select>
          </GuidedField>
          <GuidedField label="推荐设置" hint="推荐加料会在列表中优先展示。">
            <el-select v-model="extraForm.recommended">
              <el-option label="推荐" :value="true" />
              <el-option label="普通" :value="false" />
            </el-select>
          </GuidedField>
        </div>
      </article>
    </div>
    <template #footer>
      <el-button @click="emit('update:extraEditorVisible', false)">取消</el-button>
      <el-button type="primary" :loading="extraSubmitting" :disabled="!extraForm.name" @click="emit('submitExtra')">
        {{ isEditingExtra ? '保存修改' : '创建加料' }}
      </el-button>
    </template>
  </el-dialog>

  <el-dialog
    :model-value="inventoryEditorVisible"
    title="库存调整"
    width="560px"
    @update:model-value="emit('update:inventoryEditorVisible', $event)"
  >
    <div class="detail-grid detail-grid--single">
      <article class="detail-card">
        <div class="detail-actions">
          <GuidedField label="库存 SKU" hint="选择要调整库存的具体规格；编辑已有库存时不可修改。" required>
            <el-input v-model.trim="inventoryForm.skuId" placeholder="如 sku_001" :disabled="Boolean(inventoryForm.currentStock !== undefined)" />
          </GuidedField>
          <GuidedField label="适用门店" hint="不选择表示所有门店，选择后只调整指定门店。">
            <el-select v-model="inventoryStoreIds" multiple filterable placeholder="全部门店">
              <el-option v-for="store in storeOptions" :key="store.id" :label="store.name" :value="store.id" />
            </el-select>
          </GuidedField>
          <GuidedField label="调整后库存" hint="直接填写调整完成后的库存总数，不是增减数量。" required>
            <el-input v-model.number="inventoryForm.stock" type="number" placeholder="如 120" />
          </GuidedField>
          <p v-if="inventoryForm.currentStock !== undefined" class="detail-tip detail-actions__full">当前库存：{{ inventoryForm.currentStock }}</p>
        </div>
      </article>
    </div>
    <template #footer>
      <el-button @click="emit('update:inventoryEditorVisible', false)">取消</el-button>
      <el-button type="primary" :loading="inventorySubmitting" :disabled="!inventoryForm.skuId || inventoryForm.stock === undefined" @click="emit('submitInventory')">
        保存库存
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import GuidedField from '@/components/GuidedField.vue'
import LocalImagePicker from '@/components/LocalImagePicker.vue'
import type {
  AdminCategory,
  AdminComboItem,
  AdminExtra,
  AdminProduct,
  CategorySavePayload,
  ComboSavePayload,
  ExtraSavePayload,
  InventoryUpdatePayload,
  ProductSavePayload,
  ProductSpecOption,
} from '@/api/product'
import type { AdminStore } from '@/api/store'
import { MENU_TAB_LABELS, MenuTab } from '@jiaofa/constants'
import { specPresetNames } from '@/utils/product-spec'

/** 分类的菜单标识固定为经典菜单：不让运营改，避免小程序菜单 tab 分裂 */
const menuTabLabel = MENU_TAB_LABELS[MenuTab.Classic]

type InventoryForm = InventoryUpdatePayload & { currentStock?: number }

const props = defineProps<{
  productEditorVisible: boolean
  comboEditorVisible: boolean
  categoryEditorVisible: boolean
  extraEditorVisible: boolean
  inventoryEditorVisible: boolean
  productSubmitting: boolean
  comboSubmitting: boolean
  categorySubmitting: boolean
  extraSubmitting: boolean
  inventorySubmitting: boolean
  productForm: ProductSavePayload
  comboForm: ComboSavePayload
  comboItemForm: AdminComboItem
  categoryForm: CategorySavePayload
  extraForm: ExtraSavePayload
  inventoryForm: InventoryForm
  categories: AdminCategory[]
  productOptions: AdminProduct[]
  extraOptions: AdminExtra[]
  storeOptions: AdminStore[]
  comboStoreIds: string[]
  categoryStoreIds: string[]
  inventoryStoreIds: string[]
  /** 当前编辑商品的规格数量：≥2 时库存会按规格平摊，用于给运营提示 */
  productSkuCount?: number
}>()

const emit = defineEmits<{
  'update:productEditorVisible': [value: boolean]
  'update:comboEditorVisible': [value: boolean]
  'update:categoryEditorVisible': [value: boolean]
  'update:extraEditorVisible': [value: boolean]
  'update:inventoryEditorVisible': [value: boolean]
  'update:comboStoreIds': [value: string[]]
  'update:categoryStoreIds': [value: string[]]
  'update:inventoryStoreIds': [value: string[]]
  productStoreChanged: []
  submitProduct: []
  appendProductSpecOption: [groupIndex: number]
  removeProductSpecOption: [groupIndex: number, optionIndex: number]
  setProductToppingOptions: [extraIds: string[]]
  submitCombo: []
  submitCategory: []
  submitExtra: []
  submitInventory: []
  appendComboItem: []
  removeComboItem: [index: number]
}>()

const isEditingProduct = computed(() => Boolean(props.productForm.id))
const isEditingCombo = computed(() => Boolean(props.comboForm.id))
const isEditingCategory = computed(() => Boolean(props.categoryForm.id))
const isEditingExtra = computed(() => Boolean(props.extraForm.id))
const productCategoryOptions = computed(() => {
  if (!props.productForm.storeId) return []
  return props.categories.filter((category) => {
    const storeIds = getCategoryStoreIds(category)
    return !storeIds.length || storeIds.includes(props.productForm.storeId || '')
  })
})
// 多规格商品的库存是一个商品级数字：保存后平均分配到各规格，列表显示的合计与输入一致
const productStockHint = computed(() =>
  (props.productSkuCount ?? 0) >= 2
    ? `单门店口径，输入多少显示多少：该商品有 ${props.productSkuCount} 个规格，保存后按规格平均分配（合计不变）；只调某一个规格请到库存管理页按规格维护。`
    : '单门店口径，输入多少显示多少。',
)
const comboStoreIds = computed({
  get: () => props.comboStoreIds,
  set: (value: string[]) => emit('update:comboStoreIds', value),
})

const categoryStoreIds = computed({
  get: () => props.categoryStoreIds,
  set: (value: string[]) => emit('update:categoryStoreIds', value),
})
const inventoryStoreIds = computed({
  get: () => props.inventoryStoreIds,
  set: (value: string[]) => emit('update:inventoryStoreIds', value),
})

function getCategoryStoreIds(category: AdminCategory): string[] {
  if (category.storeIds?.length) return category.storeIds.map(String).map((item) => item.trim()).filter(Boolean)
  if (!category.storeId) return []
  return category.storeId
    .split(/[\n,，]/)
    .map((item) => item.trim())
    .filter(Boolean)
}

/** 套餐内各商品是否会要求顾客选糖冰：组套餐时就能看出搭配是否合适。 */
function productNeedSpecText(productId: string): string {
  const product = props.productOptions.find((item) => item.id === productId)
  if (!product) return '规格要求未知'
  return product.needSpec === false ? '不要求选糖/温度' : '要求选糖/温度'
}

function selectedToppingExtraIds(options: ProductSpecOption[] = []): string[] {
  return options.map((option) => option.id)
}

function handleProductToppingChange(value: string[] | string): void {
  emit('setProductToppingOptions', Array.isArray(value) ? value : [value])
}
</script>

<style scoped lang="scss">
.detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.detail-grid--single { grid-template-columns: 1fr; }
.detail-card { padding: 16px; border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; }
.detail-card h3 { margin: 0 0 14px; font-size: 16px; }
.detail-actions { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.detail-actions__full { grid-column: 1 / -1; }
.detail-tip { margin: 12px 0 0; color: var(--jf-color-text-secondary); font-size: 12px; }
.spec-editor { padding: 14px; border: 1px solid var(--jf-color-border); border-radius: 10px; background: rgba(252, 250, 246, .62); }
.spec-editor__heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
.spec-editor__heading div { display: flex; flex-direction: column; gap: 4px; }
.spec-editor__heading small { color: var(--jf-color-text-secondary); font-size: 12px; }
.spec-group { padding: 12px; border: 1px solid var(--jf-color-border); border-radius: 10px; background: #fff; }
.spec-group + .spec-group { margin-top: 12px; }
.spec-group__header { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.spec-option-list { margin-top: 12px; display: flex; flex-direction: column; gap: 8px; }
.spec-option { display: grid; grid-template-columns: 1fr 120px 100px 100px auto; gap: 8px; align-items: center; }
.detail-items { margin-top: 12px; display: flex; flex-direction: column; gap: 10px; }
.detail-item { padding: 12px; border-radius: 10px; background: rgba(252, 250, 246, .82); display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.detail-item__meta { min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.detail-item__meta strong { color: var(--jf-color-text-primary); }
.detail-item__meta small { color: var(--jf-color-text-secondary); }
.detail-item__right button { border: 0; background: transparent; color: var(--jf-color-primary-dark); cursor: pointer; font: inherit; }
@media (max-width: 760px) {
  .detail-grid, .detail-actions, .spec-option { grid-template-columns: 1fr; }
  .detail-actions__full { grid-column: auto; }
}
</style>
